import { AfterViewInit, Component, computed, Input, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject, combineLatest, defer, Observable, Subscription } from 'rxjs';
import { MatSort } from '@angular/material/sort';
import { MatSidenav } from '@angular/material/sidenav';
import { SelectionModel } from '@angular/cdk/collections';
import { InvoicePdfService } from '../../invoices/service/invoice-pdf.service';
import { ConfigService, ConfigState } from '@aiwyn/config';
import { PaymentPlanModalComponent, PaymentPlanModalData } from '../../modals/payment-plan-modal/payment-plan-modal.component';
import { PaymentPlanService } from '../../../../services/payment-plan/payment-plan.service';
import {
  PaymentMethodIsNotFromUserComponent,
} from '../../modals/payment-method-is-not-from-user/payment-method-is-not-from-user.component';
import {
  ConfirmDeletePaymentPlanModalComponent,
} from '../../modals/confirm-delete-payment-plan-modal/confirm-delete-payment-plan-modal.component';
import { ClientApiService, PaymentMethodType, PaymentPlanUi } from '@aiwyn/api';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { GetPaymentPlans, SettingsState } from '../state';
import { Store } from '@ngxs/store';
import { getLoadedResults, isEmpty, isLoaded, LoadableStatus } from '@aiwyn/shared';
import { CommonSettingsState } from '@aiwyn/common/settings';
import { AbstractPaymentMethod, GetClients, GetPaymentFeeConfigs, PaymentPlatformService, PaymentsState } from '@aiwyn/common/payments';
import { MatTable } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-payment-plans',
  templateUrl: './payment-plans.component.html',
  styleUrls: ['./payment-plans.component.scss'],
  standalone: false,
})
export class PaymentPlansComponent implements OnInit, AfterViewInit {

  @Input() set paymentPlanUis(paymentPlanUis: PaymentPlanUi[]) {
    this._paymentPlanUiList.next(paymentPlanUis);
  }

  private _paymentPlanUiList: BehaviorSubject<PaymentPlanUi[]> = new BehaviorSubject<PaymentPlanUi[]>([]);
  paymentPlanUiList$: Observable<PaymentPlanUi[]> = this._paymentPlanUiList.asObservable();

  queryParams$: Observable<Params> = this.activatedRoute.queryParams;

  paymentPlansUpdatedSubscription: Subscription;

  // sort for material table
  @ViewChild(MatSort) sort: MatSort;

  @ViewChild('sidenav') public sidenav: MatSidenav;

  @ViewChild(MatTable) table: MatTable<PaymentPlanUi>;

  @Input() searchString: String;

  /*
    clientId used to store URL data
    note: URL must use format specified in app-routing.module.ts
   */
  clientId: string = '';

  // columns for material table
  columnsToDisplay = ['name', 'client', 'amount', 'method', 'timeFrame', 'actions'];

  // Angular SelectionModel for selecting row on click.
  selection = new SelectionModel(true, [], true);

  hasError$ = this.store.select(SettingsState.getPaymentPlansStatus).pipe(
    map(status => status === LoadableStatus.ERROR),
  );

  savedPaymentMethodsLoaded = false;

  savedPaymentMethodIdsV5$ = new BehaviorSubject<string[]>(undefined);

  setSavedPaymentMethodIdsV5$ = this.paymentPlatformService.getSavedPaymentMethods().pipe(
    take(1),
    tap(savedPaymentMethods => this.savedPaymentMethodIdsV5$.next(savedPaymentMethods.map(({ id }) => id))),
  );

  setSavedPaymentMethodsLoaded$ = this.paymentPlatformService.getSavedPaymentMethods().pipe(
    take(1),
    tap(_ => this.savedPaymentMethodsLoaded = true),
  );

  clients = this.store.selectSignal(PaymentsState.getClients);
  clientToBusinessEntityMap = computed<Record<number, number>>(() => {
    const defaultBusinessEntityId = this.store.selectSnapshot(ConfigState.getDefaultBusinessEntity)?.id;
    const clients = this.clients();
    if (isEmpty(clients)) {
      return {};
    }

    return clients.reduce((acc, client) => ({ ...acc, [client.id]: client.businessEntityId ?? defaultBusinessEntityId }), {});
  });

  constructor(
    public invoicePdfService: InvoicePdfService,
    public dialog: MatDialog,
    public activatedRoute: ActivatedRoute,
    public router: Router,
    public paymentPlanService: PaymentPlanService,
    private spinner: NgxSpinnerService,
    private clientApiService: ClientApiService,
    public configService: ConfigService,
    private readonly store: Store,
    private readonly paymentPlatformService: PaymentPlatformService,
  ) {
    this.store.dispatch([new GetPaymentFeeConfigs(), new GetClients()]);
  }

  ngOnInit(): void {
    this.setSavedPaymentMethodsLoaded$.subscribe();
    this.setSavedPaymentMethodIdsV5$.subscribe();
    // Grabs client id list so the client dropdown can be populated. (Required before dialog can be opened)
    this.clientApiService.getClientsFromClientIdList().pipe(
      tap(clients => this.paymentPlanService.clientList = clients),
      switchMap(_ => combineLatest([this.paymentPlanUiList$, this.queryParams$])),
      tap(([paymentUiList, queryParams]) => {
        const paymentPlanUi = paymentUiList.find(paymentPlanUi =>
          paymentPlanUi.paymentPlan.id === +queryParams?.['paymentPlanId']
        );

        if (paymentPlanUi != undefined) {
          this.openModalFromRoute(true, paymentPlanUi);
        }
      }),
      take(1),
    ).subscribe();

    this.paymentPlansUpdatedSubscription = this.paymentPlanService.paymentPlansUpdated$.subscribe(() => {
      this.refreshTableData();
    });
  }

  ngAfterViewInit(): void {
    this.paymentPlanService.dataSourcePaymentPlans.sort = this.sort;
    this.paymentPlanService.dataSourcePaymentPlans.sortingDataAccessor = this.sortingFunction();
  }

  openModalFromRoute(isEditPaymentPlan: boolean, paymentPlanUi: PaymentPlanUi): void {
    defer(() => this.router.navigate(['/main/settings'], { queryParams: null })).pipe(
      switchMap(_ => combineLatest([
        getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable), true),
        this.store.select(CommonSettingsState.getClientCurrenciesStatus).pipe(
          isLoaded(true),
          switchMap(_ => this.store.selectOnce(CommonSettingsState.getClientCurrencies)),
        ),
      ])),
      tap(_ => this.spinner.hide()),
      switchMap(([autoPayClients, clientCurrencies]) => this.dialog.open<PaymentPlanModalComponent, PaymentPlanModalData>(
        PaymentPlanModalComponent,
        { data: { isEditPaymentPlan, paymentPlanUi, autoPayClients, clientCurrencies } },
      ).afterClosed()),
      take(1),
    ).subscribe();
  }

  /**
   * Opens modal to create a new or edit a payment plan (recurring payment).
   * @param isEditPaymentPlan
   * @param paymentPlanUi
   */
  openNewPaymentPlanModal(isEditPaymentPlan: boolean, paymentPlanUi: PaymentPlanUi): void {
    combineLatest([
      getLoadedResults(this.store.select(CommonSettingsState.getAutoPayClientsLoadable), true),
      this.store.select(CommonSettingsState.getClientCurrenciesStatus).pipe(
        isLoaded(true),
        switchMap(_ => this.store.selectOnce(CommonSettingsState.getClientCurrencies)),
      ),
    ]).pipe(
      take(1),
      switchMap(([autoPayClients, clientCurrencies]) => this.dialog.open<PaymentPlanModalComponent, PaymentPlanModalData>(
        PaymentPlanModalComponent,
        { data: { isEditPaymentPlan, paymentPlanUi, autoPayClients, clientCurrencies } },
      ).afterClosed()),
      take(1),
    ).subscribe();
  }

  /**
   * This function only aids to deactivate the paymentPlan.
   */
  deactivatePaymentPlan(paymentPlanUi: PaymentPlanUi): void {
    this.dialog.open(ConfirmDeletePaymentPlanModalComponent, { data: { paymentPlan: paymentPlanUi.paymentPlan } });
  }

  /**
   * Function that refreshes table data when a payment plan (recurring payment) is added or edited.
   */
  refreshTableData(): void {
    this.store.dispatch(new GetPaymentPlans(true));
  }

  /**
   * This is launched when the info icon is clicked to let the user know the payment method is not theirs.
   */
  launchPaymentMethodIsNotFromUserInfoModal(): void {
    this.dialog.open(PaymentMethodIsNotFromUserComponent);
  }

  isPaymentMethodFromOtherUser(paymentMethodId: string): boolean {
    return !this.savedPaymentMethodIdsV5$.value.includes(paymentMethodId);
  }

  isDirectDebit(paymentMethodType: PaymentMethodType): boolean {
    return AbstractPaymentMethod.isDirectDebit(paymentMethodType);
  }

  /**
   * Destroy subscriptions.
   */
  ngOnDestroy(): void {
    if (this.paymentPlansUpdatedSubscription?.closed === false) {
      this.paymentPlansUpdatedSubscription.unsubscribe();
    }
  }

  /**
   * Defines sorting parameters.
   */
  sortingFunction(): (item: PaymentPlanUi, property: string) => string | number {
    const rate = this.configService.paymentPlanProcessingFeeRate;
    return function (item: PaymentPlanUi, property: string) {
      switch (property) {

        case 'amount': {
          return item.paymentPlan.paymentMethod.toLowerCase() === 'credit'
            ? item.paymentPlan.amount + (item.paymentPlan.amount * rate)
            : item.paymentPlan.amount;
        }

        case 'method': {
          return item.paymentPlan.cardType + item.paymentPlan.last4;
        }

        case 'timeFrame': {
          return item.paymentPlan.startDate;
        }

        default: {
          return item['paymentPlan'][property];
        }
      }
    }
  }
}
